import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const links = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Impact", href: "#impact" },
  { label: "Engage", href: "#engage" },
  { label: "Contact", href: "#contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || open
            ? "bg-[#050505]/95 backdrop-blur-xl border-b border-[#1a1a1a]"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-8 lg:px-12 flex items-center justify-between h-16 lg:h-20">
          <a
            href="#home"
            data-testid="nav-logo"
            className="font-serif-display text-xl md:text-2xl text-[#F5F5F0] tracking-tight relative z-10"
            onClick={() => setOpen(false)}
          >
            Denis<span className="text-[#C5A880]">.</span>Antony
          </a>

          {/* Desktop nav — visible from lg (≥1024px) */}
          <nav className="hidden lg:flex items-center gap-8 xl:gap-9">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="nav-link-underline text-[12px] font-medium tracking-[0.2em] uppercase text-[#A3A39A] hover:text-[#F5F5F0] transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3 relative z-10">
            <a
              href="mailto:hello@denisantony.com"
              data-testid="nav-cta-email"
              className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 bg-[#C5A880] text-[#050505] text-[11px] font-semibold tracking-[0.2em] uppercase hover:bg-[#D8BE96] transition-colors"
            >
              Work With Me
            </a>
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 -mr-2 text-[#F5F5F0] hover:text-[#C5A880] transition-colors"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-menu-drawer"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile / tablet menu — sibling of header so nested-fixed quirks are avoided */}
      <div
        id="mobile-menu-drawer"
        data-testid="mobile-menu"
        aria-hidden={!open}
        className={`lg:hidden fixed inset-0 z-40 bg-[#050505] transition-all duration-300 ${
          open
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="h-full overflow-y-auto pt-20 pb-10 px-5 sm:px-6 md:px-8">
          <nav className="flex flex-col">
            {links.map((l, i) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="flex items-baseline gap-5 py-5 text-[#F5F5F0] text-3xl sm:text-4xl font-serif-display font-light border-b border-[#1a1a1a] hover:text-[#C5A880] transition-colors"
              >
                <span className="text-[#C5A880] text-[11px] tracking-[0.3em] flex-shrink-0">
                  0{i + 1}
                </span>
                <span>{l.label}</span>
              </a>
            ))}
          </nav>

          <a
            href="mailto:hello@denisantony.com"
            onClick={() => setOpen(false)}
            data-testid="mobile-nav-cta"
            className="mt-10 inline-flex items-center justify-center px-6 py-4 min-h-[52px] bg-[#C5A880] text-[#050505] text-[12px] font-semibold tracking-[0.25em] uppercase w-full hover:bg-[#D8BE96] transition-colors"
          >
            Work With Me
          </a>

          <div className="mt-8 pt-6 border-t border-[#1a1a1a] space-y-2">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#666]">
              Direct
            </p>
            <p className="text-[#F5F5F0] text-base">hello@denisantony.com</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Nav;
